/**
 * Game Effects — typed effect descriptors returned by game logic.
 *
 * Game logic (phases.ts, death.ts, reset.ts) returns GameEffect[] instead of
 * calling renderers directly. The main loop calls applyEffects() to dispatch
 * each effect to the appropriate renderer/system.
 *
 * This decouples game state mutation from visual feedback, enabling multiplayer
 * where the host runs game logic and clients independently render effects.
 */

import type { Camera } from 'bonkjs';
import type { ParticleSystem } from '../rendering/particles';
import type { ScreenEffects } from '../rendering/effects';
import type { ScreenShake } from '../rendering/screen-shake';
import type { HUDRenderer } from '../rendering/hud';
import type { VoidRenderer } from '../rendering/void';
import type { BackgroundRenderer } from '../rendering/background';
import type { TerrainRenderer } from '../terrain/rendering';
import type { TankVisual } from '../rendering/tanks';
import { triggerTankFlash } from '../rendering/tanks';
import type { DIRenderer } from '../rendering/di';
import type { BurstRenderer } from '../rendering/burst';
import type { TargetRenderer } from '../rendering/targets';
import type { LotteryRenderer } from '../rendering/lottery';
import type { AnnouncementRenderer } from '../rendering/announcements';
import type { VortexHoleRenderer } from '../rendering/vortex-holes';
import type { CardDrawRenderer } from '../rendering/roguelite/card-draw-renderer';
import type { RogueliteHUDRenderer } from '../rendering/roguelite/run-hud';
import type { Projectile, Target } from '../types';
import type { GridBounds } from '../terrain/grid';
import { WORLD } from '../constants';
import { getActiveTheme } from '../stages';
import { playSound } from '../audio';

// ─── Effect Types ────────────────────────────────────────────────────────────

export type GameEffect =
    // Camera
    | { type: 'camera_follow'; x: number; y: number }
    | { type: 'camera_shake'; intensity: number; decay: number }
    // Screen
    | { type: 'screen_shake'; intensity: number; decay: number }
    | { type: 'screen_flash'; color: number; alpha: number }
    | { type: 'screen_multi_flash'; color: number; alpha?: number }
    // Particles — generic dispatcher to ParticleSystem methods
    | { type: 'particles'; method: string; args: unknown[] }
    // HUD
    | { type: 'hud_update_turn_timer'; time: number }
    | { type: 'hud_hide_turn_timer' }
    | { type: 'hud_round_start'; progress: number; roundNumber: number; turnNumber: number; visible: boolean }
    | { type: 'hud_show_eliminated'; x: number; y: number; color: number }
    | { type: 'hud_show_void_eliminated'; x: number; y: number }
    | { type: 'hud_show_sky_eliminated'; x: number; y: number; color: number }
    | { type: 'hud_show_turn_dialog'; playerIndex: number; color: number }
    // Terrain rendering
    | { type: 'terrain_update'; bounds: GridBounds; skipHotEdge?: boolean }
    | { type: 'terrain_rebuild' }
    // Terrain crater glow
    | { type: 'terrain_add_crater'; x: number; y: number; radius: number }
    // Void
    | { type: 'void_edge_distortion'; x: number; y: number }
    | { type: 'void_consume_burst'; x: number; y: number }
    | { type: 'void_foreshadow'; x: number; y: number }
    | { type: 'void_rise_effects'; voidY: number }
    | { type: 'void_mood_update'; mood: number }
    // Tank visual
    | { type: 'tank_flash'; playerIndex: number; bright: number; dark: number; duration: number }
    // DI overlay
    | { type: 'di_show' }
    | { type: 'di_hide' }
    // Burst overlay
    | { type: 'burst_show' }
    | { type: 'burst_hide' }
    // Edgeguard overlay
    | { type: 'edgeguard_show' }
    | { type: 'edgeguard_hide' }
    | { type: 'edgeguard_fired'; angle: number; x: number; y: number; targets: Array<{ playerIndex: number; x: number; y: number; momentum: number; damagePercent: number }> }
    | { type: 'burst_resolved'; playerIndex: number; vx: number; vy: number; x: number; y: number; momentum: number; weaponCharge: number; hitstunFrames: number; braced: boolean; damagePercent: number }
    // Special (voluntary burst trigger)
    | { type: 'special_triggered'; playerIndex: number }
    // Projectile rendering
    | { type: 'render_projectiles'; projectiles: Projectile[]; wind: number }
    // Cosmic Lottery
    | { type: 'lottery_show'; playerIndex: number; x: number; y: number; slots: import('../weapons').WeaponDef[] }
    | { type: 'lottery_lock'; slotIndex: number; weapon: import('../weapons').WeaponDef }
    | { type: 'lottery_pick'; slotIndex: number }
    | { type: 'lottery_hide' }
    // Tank select — confirmed tank for multiplayer sync
    | { type: 'tank_select_confirmed'; playerIndex: number; tankId: string }
    // Stage select — confirmed stage for multiplayer sync
    | { type: 'stage_select_confirmed'; stageId: string; seed?: number; themeId?: string }
    // Phase transition — for multiplayer fire/DI detection
    | { type: 'phase_transition'; from: string; to: string }
    // Network sync — emitted at turn boundaries for state hash comparison
    | { type: 'turn_boundary_checkpoint' }
    // Firing resolve — carries pending knockback state for multiplayer sync (multi-hit weapons)
    | { type: 'firing_resolved'; players: Array<{ playerIndex: number; x: number; y: number; diPendingVx: number; diPendingVy: number; kbStackHitCount: number; damagePercent: number; weaponCharge: number }>; diOpened: boolean }
    // DI resolve — carries post-DI velocity for multiplayer sync
    | { type: 'di_resolved'; playerIndex: number; vx: number; vy: number; x: number; y: number; hitstunFrames: number }
    // Defense phase — start/end for multiplayer sync
    | { type: 'defense_start'; playerIndex: number }
    | { type: 'defense_resolved'; playerIndex: number; x: number; y: number; vx: number; vy: number; tumble: boolean; hitstunFrames: number; tumbleRecoveryFrame: number; tumbleRecoveryMax: number; momentum: number; weaponCharge: number; settled: boolean; grounded: boolean; liftFuel: number; liftActive: boolean; fastFallActive: boolean; landingTimer: number; landingTimerMax: number; defenseSubPhase: 'air' | 'landing' | 'none'; onWireframe: boolean }
    // Terrain resync — trigger terrain re-render after resync snapshot
    | { type: 'terrain_resync' }
    // Stage theme — update all renderers to new stage colors
    | { type: 'stage_theme_change'; stageId: string }
    // Brace heal flash — green damage text flash
    | { type: 'hud_heal_flash'; playerIndex: number }
    // Target Blast — target destroyed
    | { type: 'target_hit'; x: number; y: number; radius: number }
    // Target Blast — rebuild target visuals
    | { type: 'targets_rebuild'; targets: Target[] }
    // Target Blast — intro sequence text
    | { type: 'hud_target_blast_intro'; step: number; timer: number }
    // Target Blast — exit gate
    | { type: 'exit_gate_activated'; x: number; y: number }
    | { type: 'exit_gate_entered'; x: number; y: number }
    // Vortex tentacle lash visual
    | { type: 'tentacle_lash'; portalX: number; portalY: number; targetX: number; targetY: number }
    // Announcements — bold fighting-game text popups
    | { type: 'announce_screen'; text: string; color: number; fontSize: number; hold?: number; y?: number; x?: number; delay?: number }
    | { type: 'announce_world'; text: string; x: number; y: number; color: number; fontSize: number; hold?: number }
    // Background scene reactivity (sway stalactites, bamboo, vines, wireframe shapes)
    | { type: 'bg_explosion'; x: number; y: number; radius: number }
    // Roguelite card draw
    | { type: 'roguelite_card_draw_show' }
    | { type: 'roguelite_card_draw_hide' }
    | { type: 'roguelite_card_draw_cursor'; cursor: number }
    | { type: 'roguelite_zone_enter'; zone: number }
    // Death authority — authoritative death trigger for multiplayer sync
    | { type: 'death_triggered'; playerIndex: number; deathType: 'void' | 'sky' | 'hp' | 'side'; x: number; y: number }
    // Tutorial
    | { type: 'tutorial_prompt'; text: string; subText?: string; inputHints: string[] }
    | { type: 'tutorial_success' }
    | { type: 'tutorial_complete' }
    | { type: 'tutorial_step_advance'; step: number }
    // Survival — trigger AI controller rebuild after enemy respawn
    | { type: 'rebuild_ai' }
    | { type: 'brake_triggered'; playerIndex: number }
    | { type: 'hud_show_side_eliminated'; x: number; y: number; color: number };

// ─── Phase Transition Helper ────────────────────────────────────────────────

/** Set state.phase and emit a phase_transition effect for multiplayer sync. */
export function transitionPhase(
    state: import('../types').GameState,
    to: import('../types').Phase,
    fx: GameEffect[],
): void {
    const from = state.phase;
    state.phase = to;
    fx.push({ type: 'phase_transition', from, to });
}

// ─── Renderer Bundle ─────────────────────────────────────────────────────────

export interface Renderers {
    camera: Camera;
    particles: ParticleSystem;
    effects: ScreenEffects;
    screenShake: ScreenShake;
    hud: HUDRenderer;
    voidRenderer: VoidRenderer;
    background: BackgroundRenderer;
    terrainRenderer: TerrainRenderer;
    tankVisuals: TankVisual[];
    diRenderer: DIRenderer;
    burstRenderer: BurstRenderer;
    targetRenderer: TargetRenderer;
    lotteryRenderer: LotteryRenderer;
    announcements: AnnouncementRenderer;
    vortexHoleRenderer?: VortexHoleRenderer;
    cardDrawRenderer?: CardDrawRenderer;
    rogueliteHUD?: RogueliteHUDRenderer;
}

// ─── Effect Dispatcher ───────────────────────────────────────────────────────

export function applyEffects(effects: GameEffect[], r: Renderers): void {
    for (const fx of effects) {
        switch (fx.type) {
            // Camera
            case 'camera_follow': {
                const { x, y } = fx;
                r.camera.follow(() => [x, y]);
                break;
            }
            case 'camera_shake':
                r.camera.shake(fx.intensity, { decay: fx.decay });
                break;

            // Screen
            case 'screen_shake':
                r.screenShake.trigger(fx.intensity, fx.decay);
                if (fx.intensity > 15) playSound('explosion');
                else if (fx.intensity >= 5) playSound('bounce');
                break;
            case 'screen_flash':
                r.effects.triggerFlash(fx.color, fx.alpha);
                break;
            case 'screen_multi_flash':
                r.effects.triggerMultiFlash(fx.color, fx.alpha);
                break;

            // Particles — dynamic dispatch to named method
            case 'particles': {
                const method = (r.particles as unknown as Record<string, (...args: unknown[]) => void>)[fx.method];
                if (method) method.apply(r.particles, fx.args);
                break;
            }

            // HUD
            case 'hud_update_turn_timer':
                r.hud.updateTurnTimer(fx.time);
                break;
            case 'hud_hide_turn_timer':
                r.hud.hideTurnTimer();
                break;
            case 'hud_round_start':
                r.hud.updateRoundStart(fx.progress, fx.roundNumber, fx.visible);
                break;
            case 'hud_show_eliminated':
                r.hud.showEliminated(fx.x, fx.y, fx.color);
                break;
            case 'hud_show_void_eliminated':
                r.hud.showVoidEliminated(fx.x, fx.y);
                playSound('death');
                break;
            case 'hud_show_sky_eliminated':
                r.hud.showSkyEliminated(fx.x, fx.y, fx.color);
                playSound('death');
                break;
            case 'hud_show_turn_dialog':
                playSound('turn_transition');
                break;
            case 'hud_heal_flash':
                r.hud.triggerHealFlash(fx.playerIndex);
                break;

            // Terrain
            case 'terrain_update':
                r.terrainRenderer.updateAfterDestruction(fx.bounds, fx.skipHotEdge);
                break;
            case 'terrain_rebuild':
                r.terrainRenderer.buildFull();
                // Update camera bounds for dynamic world height (ascent mode = 8000px)
                if (r.camera.bounds) r.camera.bounds.maxY = WORLD.VIRTUAL_HEIGHT;
                break;

            // Terrain crater glow
            case 'terrain_add_crater':
                r.terrainRenderer.addCrater(fx.x, fx.y, fx.radius);
                break;

            // Void
            case 'void_edge_distortion':
                r.voidRenderer.triggerEdgeDistortion(fx.x, fx.y);
                break;
            case 'void_consume_burst':
                r.voidRenderer.triggerConsumeBurst(fx.x, fx.y);
                break;
            case 'void_foreshadow':
                r.voidRenderer.triggerForeshadow(fx.x, fx.y);
                break;
            case 'void_rise_effects':
                r.voidRenderer.triggerRiseEffects(
                    fx.voidY, r.particles, r.effects, r.camera, r.screenShake,
                );
                break;
            case 'void_mood_update':
                r.voidRenderer.setMood(fx.mood);
                break;

            // Tank visual
            case 'tank_flash':
                triggerTankFlash(r.tankVisuals, fx.playerIndex, fx.bright, fx.dark, fx.duration);
                playSound('hit');
                break;

            // DI overlay
            case 'di_show':
                r.diRenderer.show();
                playSound('di_window');
                break;
            case 'di_hide':
                r.diRenderer.hide();
                break;

            // Burst overlay
            case 'burst_show':
                r.burstRenderer.show();
                playSound('burst');
                break;
            case 'burst_hide':
                r.burstRenderer.hide();
                break;

            // Projectile rendering
            case 'render_projectiles':
                r.effects.renderProjectiles(fx.projectiles, fx.wind);
                break;

            // Target hit — visual effects for target destruction
            case 'target_hit':
                r.particles.spawnRichExplosion(fx.x, fx.y, 0x44ff44, fx.radius * 2, 0.6);
                r.effects.triggerFlash(0x44ff44, 0.08);
                r.screenShake.trigger(4, 0.85);
                break;

            // Target Blast — rebuild target visuals after reset/generation
            case 'targets_rebuild':
                r.targetRenderer.rebuild(fx.targets);
                break;

            // Target Blast — intro text (handled in HUD update, no-op here)
            case 'hud_target_blast_intro':
                r.hud.updateTargetBlastIntro(fx.step, fx.timer);
                break;

            // Target Blast — exit gate activation
            case 'exit_gate_activated':
                r.targetRenderer.setExitGateActive(true);
                r.effects.triggerFlash(0x44ff88, 0.15);
                r.screenShake.trigger(8, 0.85);
                r.particles.spawnRichExplosion(fx.x, fx.y, 0x44ff88, 50, 0.6);
                break;

            // Target Blast — exit gate entered
            case 'exit_gate_entered':
                r.targetRenderer.setExitGateActive(false);
                break;

            // Cosmic Lottery
            case 'lottery_show':
                r.lotteryRenderer.show(fx.playerIndex, fx.x, fx.y, fx.slots);
                // Sound stub: reel spin start
                if (import.meta.env.DEV) console.log('[SFX] lottery_spin_start');
                break;
            case 'lottery_lock':
                r.lotteryRenderer.lock(fx.slotIndex);
                if (fx.slotIndex >= 3) {
                    r.lotteryRenderer.enterPick();
                }
                // Sound stub: slot lock thunk
                if (import.meta.env.DEV) console.log(`[SFX] lottery_lock_${fx.slotIndex}`);
                break;
            case 'lottery_pick':
                r.lotteryRenderer.pick(fx.slotIndex);
                // Sound stub: pick confirm
                if (import.meta.env.DEV) console.log(`[SFX] lottery_pick_${fx.slotIndex}`);
                break;
            case 'lottery_hide':
                r.lotteryRenderer.hide();
                break;

            // Edgeguard
            case 'edgeguard_show':
            case 'edgeguard_hide':
                break;
            case 'edgeguard_fired':
                playSound('edgeguard_beam');
                break;

            // Vortex tentacle lash visual
            case 'tentacle_lash':
                r.vortexHoleRenderer?.triggerTentacleLash(fx.portalX, fx.portalY, fx.targetX, fx.targetY);
                break;

            // Announcements — bold fighting-game text popups
            case 'announce_screen':
                playSound('announcement');
                r.announcements.showScreenText(fx.text, {
                    color: fx.color,
                    fontSize: fx.fontSize,
                    hold: fx.hold,
                    y: fx.y,
                    x: fx.x,
                    delay: fx.delay,
                });
                break;
            case 'announce_world':
                r.announcements.showWorldText(fx.text, fx.x, fx.y, {
                    color: fx.color,
                    fontSize: fx.fontSize,
                    hold: fx.hold,
                });
                break;

            // Background scene reactivity
            case 'bg_explosion':
                r.background.onExplosion(fx.x, fx.y, fx.radius);
                break;

            // Network sync — handled by multiplayer code in main.ts, sound hooks here
            case 'phase_transition':
                if (fx.to === 'firing') playSound('fire');
                break;
            case 'turn_boundary_checkpoint':
            case 'di_resolved':
            case 'burst_resolved':
            case 'defense_start':
            case 'defense_resolved':
            case 'special_triggered':
            case 'tank_select_confirmed':
            case 'stage_select_confirmed':
            case 'death_triggered':
                break;

            // Roguelite card draw — rendering is state-driven (CardDrawRenderer reads phase)
            case 'roguelite_card_draw_show':
            case 'roguelite_card_draw_hide':
            case 'roguelite_card_draw_cursor':
            case 'roguelite_zone_enter':
                break;

            case 'terrain_resync':
                r.terrainRenderer.buildFull();
                break;

            // Tutorial — handled by TutorialRenderer in render loop, no-op here
            case 'tutorial_prompt':
            case 'tutorial_success':
            case 'tutorial_complete':
            case 'tutorial_step_advance':
                break;

            // Brake (defense mechanic)
            case 'brake_triggered':
                break;

            // Side death HUD
            case 'hud_show_side_eliminated':
                r.hud.showSkyEliminated(fx.x, fx.y, fx.color); // reuse sky eliminated visual
                playSound('death');
                break;

            // Stage theme — update all renderers to new stage palette
            case 'stage_theme_change': {
                const theme = getActiveTheme(fx.stageId);
                r.terrainRenderer.setTheme(fx.stageId);
                r.terrainRenderer.setGridVisible(theme.showBackgroundGrid);
                r.voidRenderer.setTheme(fx.stageId);
                r.background.setTheme(fx.stageId);
                break;
            }
        }
    }
}
